import {
  formatRelativeDay,
  formatTimeLabel,
  spendingDeltaSen,
  type Category,
  type Transaction,
} from '@wis/shared';
import { Money, SourceBadge, SyncBadge, TypeBadge } from './primitives.js';

export interface TransactionRowProps {
  transaction: Transaction;
  categories: readonly Category[];
  timeZone: string;
  onSelect?: (transaction: Transaction) => void;
  /** Show the day as well as the time -- off inside a day-grouped list. */
  showDate?: boolean;
  syncState?: 'local' | 'pending' | 'synced' | 'failed';
  now?: Date;
}

export function TransactionRow({
  transaction,
  categories,
  timeZone,
  onSelect,
  showDate = true,
  syncState = 'local',
  now,
}: TransactionRowProps): JSX.Element {
  const category = categories.find((entry) => entry.id === transaction.categoryId);
  const excluded = spendingDeltaSen(transaction, categories) === 0;
  const merchant = transaction.merchantNormalized || transaction.merchantOriginal || 'No merchant';

  const when = showDate
    ? `${formatRelativeDay(transaction.occurredAt, now ?? new Date(), timeZone)}, ${formatTimeLabel(transaction.occurredAt, timeZone)}`
    : formatTimeLabel(transaction.occurredAt, timeZone);

  // Badges get their own line rather than competing with the merchant name for
  // room. On a 320px screen the first line would otherwise clip them away, and
  // a source badge nobody can read is not a source badge.
  const hasBadges =
    transaction.type !== 'expense' ||
    transaction.source !== 'manual' ||
    transaction.reviewStatus === 'needs-review' ||
    syncState === 'pending' ||
    syncState === 'failed';

  const body = (
    <>
      <span className="tx-row__icon" aria-hidden="true">
        {category?.icon ?? '•'}
      </span>
      <span className="tx-row__body">
        <span className="tx-row__merchant">{merchant}</span>
        <span className="tx-row__meta">
          <span>{category?.name ?? 'Uncategorised'}</span>
          <span aria-hidden="true">·</span>
          <span>{when}</span>
        </span>
        {hasBadges ? (
          <span className="tx-row__badges">
            <TypeBadge type={transaction.type} />
            {transaction.source === 'manual' ? null : (
              <SourceBadge source={transaction.source} />
            )}
            <SyncBadge state={syncState} />
            {transaction.reviewStatus === 'needs-review' ? (
              <span className="badge badge--review">Check</span>
            ) : null}
          </span>
        ) : null}
      </span>
      <span className="tx-row__amount">
        <Money
          sen={transaction.type === 'refund' ? -transaction.amountSen : transaction.amountSen}
          size="medium"
          currency={transaction.currency}
          excluded={excluded}
        />
      </span>
    </>
  );

  if (!onSelect) {
    return <div className="tx-row">{body}</div>;
  }

  return (
    <button
      type="button"
      className="tx-row"
      onClick={() => onSelect(transaction)}
      aria-label={`${merchant}, ${transaction.amountSen / 100} ringgit, ${when}. Edit.`}
    >
      {body}
    </button>
  );
}
